import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { find, propEq } from "ramda";

// ------------------------------------------------------------------------

import {
    ACTIVITY,
    ALCOHOL_RISK,
    BLOOD_PRESSURE,
    BODY_COMPOSITION,
    BODY_FAT,
    BODY_MASS_INDEX,
    DIABETES,
    HDL_CHOLESTEROL,
    HEART_AGE,
    HEART_RATE,
    HEIGHT,
    HRA_EMOTIONAL_SCORE,
    HRA_LIFESTYLE_SCORE,
    HRA_SOCIAL_SCORE,
    MUSCLE_MASS,
    NON_FASTING_GLUCOSE,
    QRISK3,
    SMOKING,
    STRESS_PSS4,
    TDEE,
    TEN_YEAR_CVD_RISK,
    TOTAL_CHOLESTEROL,
    WEIGHT
} from "constants/trend-charts";
import { PORTAL_IMAGE } from "constants/imagePath";
import { UnitConverter } from "@sisuwellness/utilities";
import { MeasurementSystems } from "@sisuwellness/utilities/constants/locale";
import formatTargetWeightRange from "@sisuwellness/utilities/Guidelines/Weight/formatTargetWeightRange";
import getStaticRecommendations from "constants/recommendations";
import routes from "constants/routes";

import getMetric, { getBPMetricBasedOnBPRecommendation } from "./metric";
import { fixedNumber } from "utilities/commonUtils";
import { calculateHeartAgeParams } from "views/HeartAge/utils";
import { calculateTotalDailyEnergyExpenditure, getTDEEMetricsWithUnitPreference } from "utilities/premium";
import { useTranslation } from "react-i18next";

import i18n from "../../i18n.js";

// ------------------------------------------------------------------------

export const getMetricCardProps = (healthCheck, type, recommendations = []) => {
    // Get Static Recommendation
    const metricRecommendation = find(propEq("risk", type === WEIGHT ? BODY_MASS_INDEX : type), recommendations) ||
        i18n.t(getStaticRecommendations(type)) || { text: "" };

    const metric =
        getMetric(type, healthCheck) || getBPMetricBasedOnBPRecommendation(type, metricRecommendation, healthCheck);

    if (!metric && type !== HEIGHT) return null;

    return { metricRecommendation, metric };
};

// ------------------------------------------------------------------------

const HealthMetrics = ({ children, healthCheck = {}, type }) => {
    const { t } = useTranslation();

    let {
        hr,
        bmi,
        dia,
        sys,
        isSmoker,
        weight,
        height,
        bodyFatPercent,
        ausdriskScoreValue,
        qriskScore,
        alcoholScore,
        totalCholesterol,
        hdlCholesterol,
        nonFastingGlucose,
        pss4Score,
        recommendations,
        activityRating,
        HRASocialScore,
        HRAEmotionalScore,
        HRALifestyleScore,
        muscleMass,
        nhsEthnicity,
        ireEthnicity,
        isPregnant
    } = healthCheck;

    const { heartAge = null, tenYearCvdRisk } = calculateHeartAgeParams(healthCheck);

    const {
        settings: { unitMeasurement, energyUnit }
    } = useSelector(state => state);
    const metricProps = getMetricCardProps(healthCheck, type, recommendations);

    if (!metricProps) return null;

    const { metricRecommendation, metric } = metricProps;

    const activityIcon = `${PORTAL_IMAGE}/healthHub/activity.svg`;

    const isMetric = unitMeasurement === "metric";

    // Ethnicity-aware healthy weight band, shared by the BMI and WEIGHT cards.
    const targetWeightRange = formatTargetWeightRange(
        height,
        nhsEthnicity || ireEthnicity,
        unitMeasurement?.toUpperCase() === MeasurementSystems.imperial
    );
    const weightRangeInterpolation = {
        HEALTHY_WEIGHT_LOWER: targetWeightRange.lower,
        HEALTHY_WEIGHT_UPPER: targetWeightRange.upper
    };

    /**
     * BMI is not applicable during pregnancy, so a check recorded then gets the pregnancy copy
     * instead of a band-based recommendation and target range. `isPregnant` is per health check
     * (derived by the API from that check's own answer), never the citizen's latest value.
     *
     * Applies to the WEIGHT card too — a goal weight is equally inappropriate.
     */
    const recommendationTextFor = defaultText =>
        isPregnant ? t("recommendations.bmi.pregnancy") : t(defaultText, weightRangeInterpolation);

    // Metric title not in each switch case, expected null
    const localisedMetricTitle = t(metric?.title);

    switch (type) {
        case BLOOD_PRESSURE: {
            return children({
                showIcon: true,
                unit: sys && dia ? t("units.mmhg") : "",
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                value: sys && dia ? `${sys}/${dia}` : "",
                isRiskHigh: metric.isRiskHigh,
                link: routes.trend.bloodPressure,
                icon: metric.icon || `${PORTAL_IMAGE}/healthHub/bloodpressure.svg`,
                indicator: { bg: metric.color, label: metric.rating, i18nKey: metric.i18nKey }
            });
        }
        case BODY_MASS_INDEX: {
            return children({
                showIcon: true,
                title: localisedMetricTitle,
                value: fixedNumber(bmi),
                progress: metric.risk,
                link: routes.trend.bmi,
                icon: `${PORTAL_IMAGE}/healthHub/bmi.svg`,
                unit: t("units.bmi"),
                isRiskHigh: metric.isRiskHigh,
                text: recommendationTextFor(metricRecommendation.text),
                indicator: { bg: metric.color, label: metric.rating }
            });
        }
        case BODY_FAT:
            return children({
                unit: "%",
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                link: routes.trend.bodyFat,
                isRiskHigh: metric.isRiskHigh,
                value: fixedNumber(bodyFatPercent),
                indicator: { bg: metric.color, label: metric.rating }
            });
        case HEART_RATE:
            return children({
                showIcon: true,
                value: hr,
                unit: t("units.bpm"),
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                link: routes.trend.heartRate,
                icon: `${PORTAL_IMAGE}/healthHub/heartrate.svg`,
                isRiskHigh: metric.isRiskHigh,
                indicator: { bg: metric.color, label: metric.rating }
            });
        case DIABETES:
            return children({
                unit: t("units.pts"),
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                link: routes.trend.diabetes,
                value: ausdriskScoreValue,
                isRiskHigh: metric.isRiskHigh,
                indicator: { bg: metric.color, label: metric.rating }
            });
        case QRISK3:
            return children({
                unit: "%",
                title: localisedMetricTitle,
                progress: metric.risk,
                link: routes.trend.qrisk,
                value: fixedNumber(qriskScore),
                isRiskHigh: metric.isRiskHigh,
                text: metricRecommendation.text,
                indicator: { bg: metric.color, label: metric.rating }
            });
        case ALCOHOL_RISK:
            return children({
                showIcon: true,
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                value: alcoholScore,
                link: routes.trend.alcohol,
                icon: `${PORTAL_IMAGE}/healthHub/alcohol.svg`,
                isRiskHigh: metric.isRiskHigh,
                indicator: { bg: metric.color, label: metric.rating }
            });

        case TOTAL_CHOLESTEROL:
            return children({
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                value: `${totalCholesterol.value}`,
                unit: t("units.mmoll", "mmol/L"),
                isRiskHigh: metric.isRiskHigh,
                indicator: { bg: metric.color, label: metric.rating }
            });

        case HDL_CHOLESTEROL:
            return children({
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                value: `${hdlCholesterol.value}`,
                unit: t("units.mmoll", "mmol/L"),
                isRiskHigh: metric.isRiskHigh,
                indicator: { bg: metric.color, label: metric.rating }
            });

        case NON_FASTING_GLUCOSE:
            return children({
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                value: `${nonFastingGlucose.value}`,
                unit: t("units.mmoll", "mmol/L"),
                isRiskHigh: metric.isRiskHigh,
                indicator: { bg: metric.color, label: metric.rating }
            });

        case ACTIVITY:
            return children({
                showIcon: true,
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                link: routes.trend.activity,
                icon: activityIcon,
                isRiskHigh: metric.isRiskHigh,
                indicator: { bg: metric.color, label: activityRating }
            });
        case STRESS_PSS4:
            return children({
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                value: pss4Score,
                link: routes.trend.perceivedStress,
                isRiskHigh: metric.isRiskHigh,
                indicator: { bg: metric.color, label: metric.rating }
            });
        case WEIGHT: {
            let value = [fixedNumber(weight), t("units.kilograms")];
            if (!isMetric) {
                const { stones, pounds } = UnitConverter.kgToStLbs(weight);
                value = [fixedNumber(stones), t("units.stone"), fixedNumber(pounds), t("units.pounds")];
            }
            return children({
                showIcon: true,
                // Weight reuses the BMI recommendation copy, which contains the
                // healthy-weight range tokens — interpolate them here as the BMI card does.
                text: recommendationTextFor(metricRecommendation.text),
                title: localisedMetricTitle,
                progress: metric.risk,
                value: value[0],
                unit: value[1],
                secValue: value[2],
                secUnit: value[3],
                isRiskHigh: metric.isRiskHigh,
                link: routes.trend.weight,
                icon: `${PORTAL_IMAGE}/healthHub/weight.svg`,
                indicator: { bg: metric.color, label: metric.rating }
            });
        }
        case HRA_LIFESTYLE_SCORE:
            return children({
                unit: "%",
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                value: `${fixedNumber(HRALifestyleScore)}`,
                indicator: { bg: metric.color, label: metric.rating }
            });
        case HRA_EMOTIONAL_SCORE:
            return children({
                unit: "%",
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                value: `${fixedNumber(HRAEmotionalScore)}`,
                indicator: { bg: metric.color, label: metric.rating }
            });
        case HRA_SOCIAL_SCORE:
            return children({
                unit: "%",
                text: metricRecommendation.text,
                title: localisedMetricTitle,
                progress: metric.risk,
                value: `${fixedNumber(HRASocialScore)}`,
                indicator: { bg: metric.color, label: metric.rating }
            });
        case HEIGHT: {
            let value = [fixedNumber(height), t("units.centimetres")];
            if (!isMetric) {
                const { feet, inches } = UnitConverter.cmToFtInch(value[0]);
                value = [fixedNumber(feet), t("units.feet"), fixedNumber(inches), t("units.inches")];
            }
            return children({
                showIcon: true,
                value: value[0],
                unit: value[1],
                secValue: value[2],
                secUnit: value[3],
                title: t("words.height"),
                link: routes.trend.height,
                text: metricRecommendation.text,
                converter: UnitConverter.cmToFtInch,
                icon: `${PORTAL_IMAGE}/healthHub/height.svg`
            });
        }

        case SMOKING:
            return children({
                showIcon: true,
                title: localisedMetricTitle,
                text: metric.recommendation,
                link: routes.trend.smoking,
                icon: `${PORTAL_IMAGE}/healthHub/smoking.svg`,
                value: isSmoker ? t("words.smoker") : t("words.not_smoking"),
                indicator: { bg: metric.color, label: metric.rating }
            });

        case HEART_AGE:
            return children({
                showIcon: true,
                unit: t("words.years"),
                text: (metricRecommendation || {}).text,
                title: localisedMetricTitle,
                value: heartAge,
                isRiskHigh: metric.isRiskHigh,
                link: routes.heartAge,
                icon: `${PORTAL_IMAGE}/premium/heart-age-icon.svg`,
                indicator: { bg: metric.color, label: metric.rating }
            });

        case TEN_YEAR_CVD_RISK:
            return children({
                unit: "%",
                text: (metricRecommendation || {}).text,
                title: localisedMetricTitle,
                value: tenYearCvdRisk,
                isRiskHigh: metric.isRiskHigh,
                link: routes.trend.heartAge,
                indicator: { bg: metric.color, label: metric.rating }
            });

        case TDEE: {
            const data = calculateTotalDailyEnergyExpenditure(healthCheck);
            const { unitInString } = getTDEEMetricsWithUnitPreference(data, { energyUnit });

            return children({
                value: data,
                unit: unitInString,
                title: localisedMetricTitle,
                indicator: { bg: metric.color, label: metric.rating }
            });
        }

        case MUSCLE_MASS: {
            return children({
                unit: "%",
                showIcon: true,
                value: muscleMass,
                title: localisedMetricTitle,
                text: metricRecommendation.textPortal,
                icon: `${PORTAL_IMAGE}/healthHub/muscleMass.svg`,
                indicator: { bg: metric.color, label: metric.rating }
            });
        }

        case BODY_COMPOSITION: {
            return children({});
        }

        default:
            return <></>;
    }
};

// ------------------------------------------------------------------------

HealthMetrics.propTypes = {
    healthCheck: PropTypes.shape({
        bmi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isSmoker: PropTypes.bool,
        isPregnant: PropTypes.bool,
        ausdriskScoreValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        dia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sys: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        bodyFatPercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        hr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        qriskScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        alcoholScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        activityRating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        pss4Score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        HRASocialScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        HRAEmotionalScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        HRALifestyleScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        totalCholesterol: PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
        hdlCholesterol: PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
        nonFastingGlucose: PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
        muscleMass: PropTypes.number,
        nhsEthnicity: PropTypes.string,
        ireEthnicity: PropTypes.string,
        recommendations: PropTypes.array
    }).isRequired,
    children: PropTypes.any,
    type: PropTypes.string.isRequired
};

export default HealthMetrics;
